import {
  HabitSchedule,
  PlannedOccurrence,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { getFrequencyPlanningBySchedule } from './get-frequency-planning-by-schedule';
import { getRrulePlanningBySchedule } from './get-rrule-planning-by-schedule';

export type GetSchedulePlanningOptions = {
  startDate: Timestamp;
  endDate: Timestamp;
  today: Timestamp;
  weekStartsOn: WeekDays;
  uncompletedOnly?: boolean;
  sinceLastEntryOnly?: boolean;
  strictEndDate?: boolean;
};

// rrule expansion dominates list renders; keyed on the schedule object —
// identity-stable for unchanged docs — so the cache self-invalidates on edits.
const planningCache = new WeakMap<
  HabitSchedule,
  Map<string, PlannedOccurrence[]>
>();
const PLANNING_CACHE_MAX_VARIANTS = 8;

const toPlanningCacheKey = ({
  startDate,
  endDate,
  today,
  weekStartsOn,
  uncompletedOnly,
  sinceLastEntryOnly,
  strictEndDate,
}: GetSchedulePlanningOptions) =>
  [
    startDate.getTime(),
    endDate.getTime(),
    today.getTime(),
    weekStartsOn,
    uncompletedOnly ? 1 : 0,
    sinceLastEntryOnly ? 1 : 0,
    strictEndDate ? 1 : 0,
  ].join(':');

export const getSchedulePlanning = (
  schedule: HabitSchedule,
  options: GetSchedulePlanningOptions,
): PlannedOccurrence[] => {
  const cacheKey = toPlanningCacheKey(options);
  const cachedVariants = planningCache.get(schedule);
  const cached = cachedVariants?.get(cacheKey);
  if (cached) {
    // fresh array instance so caller-side mutations cannot corrupt the cache
    return cached.slice();
  }

  const {
    startDate,
    endDate,
    today,
    weekStartsOn,
    uncompletedOnly,
    sinceLastEntryOnly,
    strictEndDate,
  } = options;

  const result = schedule.frequency?.count
    ? getFrequencyPlanningBySchedule(schedule, {
        startDate,
        endDate,
        today,
        weekStartsOn,
        uncompletedOnly,
        sinceLastEntryOnly,
      })
    : getRrulePlanningBySchedule(schedule, {
        startDate,
        endDate,
        weekStartsOn,
        uncompletedOnly,
        sinceLastEntryOnly,
        strictEndDate,
      });

  const variants = cachedVariants ?? new Map<string, PlannedOccurrence[]>();
  if (!cachedVariants) {
    planningCache.set(schedule, variants);
  }
  if (variants.size >= PLANNING_CACHE_MAX_VARIANTS) {
    variants.clear();
  }
  variants.set(cacheKey, result.slice());

  return result;
};
